import { useRef, useState } from "react";
import { Upload, Download, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CrmImport = ({ isAdmin = false, users = [], onImported }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [owner, setOwner] = useState("");
  const inputRef = useRef(null);
  const employees = users.filter((u) => u.role === "employee" || u.role === "admin");

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (isAdmin && owner) fd.append("default_employee_id", owner);
      const { data } = await api.post("/crm/import/customers", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Zaimportowano ${data.created} klientów (pominięto ${data.skipped})`);
      onImported && onImported();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="crm-import" className="max-w-2xl space-y-5">
      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <div className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-amber-400" /><h3 className="font-display text-lg font-extrabold">Import klientów z Excela / CSV</h3></div>
        <p className="text-sm text-muted-foreground">Wgraj plik <b>.xlsx</b> lub <b>.csv</b> z danymi klientów. Obsługiwane kolumny: Nazwa, Nazwa skrócona, NIP, Osoba kontaktowa, E-mail, Telefon, Adres, Branża, Region, Tagi, Status{isAdmin ? ", Handlowiec e-mail" : ""}. Duplikaty (po NIP lub nazwie) są pomijane.</p>
        <Button data-testid="crm-import-template" variant="outline" size="sm" className="gap-2 font-semibold" onClick={() => downloadBlob("/crm/import/template", "szablon-import-klientow.xlsx", "szablonu")}><Download className="h-4 w-4" />Pobierz szablon</Button>
      </div>

      {isAdmin && (
        <div className="rounded-xl border border-border bg-card p-5">
          <Label className="text-xs">Domyślny właściciel (gdy brak kolumny „Handlowiec e-mail”)</Label>
          <Select value={owner || "me"} onValueChange={(v) => setOwner(v === "me" ? "" : v)}>
            <SelectTrigger data-testid="crm-import-owner" className="mt-1 max-w-xs"><SelectValue placeholder="Ja (admin)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="me">Ja (admin)</SelectItem>
              {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div
        data-testid="crm-import-drop"
        onClick={() => !busy && inputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${busy ? "border-border opacity-60" : "border-amber-400/40 cursor-pointer hover:bg-amber-400/5"}`}>
        {busy ? <Loader2 className="h-8 w-8 animate-spin text-amber-400" /> : <Upload className="h-8 w-8 text-amber-400" />}
        <p className="font-semibold">{busy ? "Importowanie…" : "Kliknij, aby wybrać plik .xlsx lub .csv"}</p>
        <input ref={inputRef} data-testid="crm-import-input" type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onFile(f); }} />
      </div>

      {result && (
        <div data-testid="crm-import-result" className="rounded-xl border border-border bg-card p-5 space-y-2">
          <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-5 w-5" /><span className="font-bold">Import zakończony</span></div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg bg-secondary p-3"><p className="font-display text-2xl font-extrabold text-emerald-400">{result.created}</p><p className="text-xs text-muted-foreground">dodano</p></div>
            <div className="rounded-lg bg-secondary p-3"><p className="font-display text-2xl font-extrabold text-amber-300">{result.skipped}</p><p className="text-xs text-muted-foreground">pominięto (duplikaty)</p></div>
            <div className="rounded-lg bg-secondary p-3"><p className="font-display text-2xl font-extrabold">{result.total}</p><p className="text-xs text-muted-foreground">wierszy w pliku</p></div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/10 p-3">
              <div className="flex items-center gap-2 text-rose-300 text-sm font-bold"><AlertTriangle className="h-4 w-4" />Błędy ({result.errors.length})</div>
              <ul className="mt-1 list-disc pl-5 text-xs text-rose-200">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
